import { DateTime, type Zone } from "luxon";

export class AppDate {
  private _timeZone: Zone
  private _dateTime: DateTime
  private dateTimeCache: string | null = null

  constructor(dateTime?: DateTime) {
    this._timeZone = DateTime.now().zone
    this._dateTime = (dateTime ?? DateTime.now()).setZone(this._timeZone.name)
  }

  static now(): AppDate {
    return new AppDate()
  }

  static fromLuxon(dt: DateTime): AppDate {
    return new AppDate(dt.toUTC())
  }

  toUTC(): DateTime {
    return this._dateTime.toUTC()
  }

  toLuxon(): DateTime {
    return this._dateTime
  }

  startOfDay(): AppDate {
    return new AppDate(this._dateTime.startOf("day"))
  }

  midDay(): AppDate {
    this._dateTime = this._dateTime.set({ hour: 12, minute: 0 })
    return this
  }

  get dateTime(): DateTime {
    return this._dateTime
  }

  set dateTime(newValue: DateTime) {
    this._dateTime = newValue
  }

  public toString(): string {
    return this.toFormattedString(DateTime.DATETIME_FULL)
  }

  public parseDate(date: string): void {
    this._dateTime = DateTime.fromISO(date)
  }

  public toDate(): string {
    return this.toFormattedString(DateTime.DATE_FULL)
  }

  public toTime(): string {
    return this.toFormattedString(DateTime.TIME_SIMPLE)
  }

  public timeAgo(): string {
    return this._dateTime.toRelative({ base: DateTime.now() }) ?? ""
  }

  public toISODate(): string {
    return this._dateTime.toISODate() ?? ""
  }

  private toFormattedString(format: Intl.DateTimeFormatOptions): string {
    if (format === DateTime.DATE_FULL) {
      return this._dateTime.toFormat("h:mm a ZZZZ")
    }

    const tzString = this._dateTime.toLocaleString(format)
    if (this.dateTimeCache === null) {
      this.dateTimeCache = tzString
    }
    return tzString
  }

  public dateTimeInMs(date: string) {
    return DateTime.fromISO(date).setZone(this._timeZone.name).toMillis()
  }
}
